import React, { useEffect, useRef } from 'react';
import { RISK_LABEL, failureMessage } from './vendorLabels';

// Small presentational pieces shared by the vendor list, detail and queues.
// Vendor text is always rendered as React text nodes, never as HTML.

// An empty Odoo field. A dash reads as "nothing here" in a dense table without
// repeating "Not set" down every row; screen readers still hear the words.
export function EmptyValue({ label = 'Not set' }) {
  return (
    <span className="vd-empty-val">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

// `labelled` is for places with no column header (the detail header), where a bare
// "High" or a dash would not say what it measures.
export function RiskBadge({ level, labelled = false }) {
  if (!level) {
    return labelled ? <span className="vd-muted">Risk not assessed</span> : <EmptyValue label="Risk not assessed" />;
  }
  const text = RISK_LABEL[level] || level;
  return <span className={`vd-risk vd-risk--${level}`}>{labelled ? `${text} risk` : text}</span>;
}

// Status flags from spec 4.2. "NDA missing" only for high/critical vendors
// whose NDA is not signed (D31).
export function vendorFlags(v) {
  const flags = [];
  if (v.outside_change_since) flags.push('Changed outside Nexus');
  if (v.review_status === 'overdue') flags.push('Review overdue');
  if ((v.risk_level === 'high' || v.risk_level === 'critical') && v.nda_status !== 'signed') flags.push('NDA missing');
  if (v.active === false) flags.push('Archived');
  return flags;
}

export function VendorFlags({ vendor }) {
  const flags = vendorFlags(vendor);
  if (!flags.length) return null;
  return (
    <ul className="vd-chips" aria-label="Flags">
      {flags.map((f) => <li key={f} className="vd-chip vd-chip--flag">{f}</li>)}
    </ul>
  );
}

export function SpecialityChips({ items, emptyLabel = 'Untagged' }) {
  if (!items || !items.length) return <span className="vd-muted">{emptyLabel}</span>;
  return (
    <ul className="vd-chips" aria-label="Specialities">
      {items.map((s) => <li key={s.id} className="vd-chip">{s.name}</li>)}
    </ul>
  );
}

export function Skeleton({ rows = 5, label = 'Loading' }) {
  return (
    <div className="vd-skeleton" aria-busy="true" role="status">
      <span className="sr-only">{label}…</span>
      {Array.from({ length: rows }, (_, i) => <div key={i} className="vd-skeleton-row" />)}
    </div>
  );
}

export function FullError({ message, onRetry }) {
  return (
    <div className="vd-error" role="alert">
      <p>{message}</p>
      {onRetry && <button type="button" className="btn btn-secondary" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function InlineError({ message }) {
  return <p className="vd-inline-error" role="alert">{message}</p>;
}


// Overlays stack (a confirm Dialog opens over a Drawer). Only the top-most one
// answers Escape, so one keypress closes one layer.
const overlayStack = [];
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keyboard paths for drawers and dialogs, as in UserDetailDrawer: Escape
// closes, focus moves in on open, and returns to the trigger on close.
function useOverlayA11y(active, onClose, ref) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!active) return undefined;
    const token = {};
    overlayStack.push(token);
    const trigger = document.activeElement;
    const el = ref.current;
    const first = el && el.querySelector(FOCUSABLE);
    if (first) first.focus();
    else if (el) { el.setAttribute('tabindex', '-1'); el.focus(); }
    const onKey = (e) => {
      if (e.key === 'Escape' && overlayStack[overlayStack.length - 1] === token) {
        e.preventDefault();
        closeRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = overlayStack.indexOf(token);
      if (i >= 0) overlayStack.splice(i, 1);
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }, [active, ref]);
}

// Side drawer with the page-dimming backdrop (also on desktop). `subtitle` is an
// optional line under the title saying what the drawer is for.
export function Drawer({ open, title, onClose, children, wide = false, subtitle = null }) {
  const ref = useRef(null);
  useOverlayA11y(open, onClose, ref);
  return (
    <>
      <div className={`vd-backdrop ${open ? 'is-open' : ''}`} onClick={onClose} aria-hidden="true" />
      <aside
        ref={ref}
        className={`vd-drawer ${open ? 'is-open' : ''} ${wide ? 'vd-drawer--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!open}
      >
        {open && (
          <>
            <header className={`vd-drawer-head ${subtitle ? 'vd-drawer-head--sub' : ''}`}>
              <div className="vd-drawer-titles">
                <h2>{title}</h2>
                {subtitle && <p className="vd-drawer-sub">{subtitle}</p>}
              </div>
              <button type="button" className="vd-drawer-close" aria-label="Close" onClick={onClose}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </header>
            <div className="vd-drawer-body">{children}</div>
          </>
        )}
      </aside>
    </>
  );
}

// Centered confirm/prompt dialog using the shared modal classes.
export function Dialog({ title, onClose, children, labelledBy = 'vd-dialog-title' }) {
  const ref = useRef(null);
  useOverlayA11y(true, onClose, ref);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div ref={ref} className="modal vd-dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy} onClick={(e) => e.stopPropagation()}>
        <h2 id={labelledBy}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

// Why a proposal ended the way it did: a failed one names its failure_reason, a rejected one
// shows the reviewer's reason as text. Nothing for any other status.
export function ProposalOutcome({ proposal }) {
  if (proposal.status === 'failed') return <div className="vd-list-meta">{failureMessage(proposal.failure_reason)}</div>;
  if (proposal.status === 'rejected' && proposal.reject_reason) {
    return <div className="vd-list-meta">Reason: {proposal.reject_reason}</div>;
  }
  return null;
}
